import { NumberDecimalFormat } from '@components/shared/Format'
import { Constants } from '@models/Constants'
import { Country, CountryData2024 } from '@models/Country'
import { PrqGrowthValue } from '@sections/PersonalGroupGrowthSection'
import { Store } from '@stores/Store'

export class PersonalGroupGrowthIncentiveCalculation {
  store!: Store

  constructor(store: Store) {
    this.store = store
  }

  // Personal Group Growth incentive

  get personalGroupGrowthPerformanceBonusMonthlyMultipier(): number {
    return Constants.PERSONAL_GROUP_GROWTH_PERFORMANCE_BONUS_MONTHLY_MULTIPLIER
  }

  get personalGroupGrowthBonusAnualMultipier(): number {
    const { store } = this
    const { personalGroupGrowthStore, countryData, versionHelper } = store
    const { version2024 } = versionHelper
    const { personalGroupGrowthPrqGrowth } = personalGroupGrowthStore

    if (version2024) {
      const data = countryData as CountryData2024
      /* eslint-disable indent */
      switch (personalGroupGrowthPrqGrowth) {
        case PrqGrowthValue.MAINTAIN: { return data.personalGroupGrowth.multiplierMaintain }
        case PrqGrowthValue.PRQS_1_TO_2: { return data.personalGroupGrowth.multiplier1to2! }
        case PrqGrowthValue.PRQS_3_TO_5: { return data.personalGroupGrowth.multiplier3to5! }
        case PrqGrowthValue.PRQS_6_PLUS: { return data.personalGroupGrowth.multiplier6plus! }
        default: { return 0 }
      }
    }

    return 0
  }

  get personalGroupGrowthMaintainingBonusAnnualMultipier(): number {
    const { store } = this
    const { personalGroupGrowthStore, countryData, versionHelper } = store
    const { version2024 } = versionHelper
    const { personalGroupGrowthPrqCurrent } = personalGroupGrowthStore

    if (personalGroupGrowthPrqCurrent < 12) { return 0 }

    if (version2024) {
      const data = countryData as CountryData2024
      return data.personalGroupGrowth.multiplierMaintain12!
    }

    return 0
  }

  get personalGroupGrowthAmount(): number {
    const { store } = this
    const { personalGroupGrowthStore } = store
    const { personalGroupGrowthMonthlyAmount, personalGroupGrowthAnnualTotalIncentive, personalGroupGrowthExtraAnnualMaintainIncentive } = this
    const { personalGroupGrowthActive } = personalGroupGrowthStore
    if (!personalGroupGrowthActive) { return 0 }

    if (personalGroupGrowthMonthlyAmount > 0) {
      const { personalGroupGrowthPrqCurrent } = personalGroupGrowthStore
      return personalGroupGrowthMonthlyAmount * personalGroupGrowthPrqCurrent + personalGroupGrowthAnnualTotalIncentive + personalGroupGrowthExtraAnnualMaintainIncentive
    }

    return 0
  }

  get personalGroupGrowthExtraAnnualMaintainIncentive(): number {
    const { store } = this
    const { performanceBonus, personalGroupGrowthStore, countryData, versionHelper } = store
    const { version2024 } = versionHelper
    const { personalGroupGrowthMaintaining, personalGroupGrowthPrqCurrent } = personalGroupGrowthStore

    if (version2024) {
      const data = countryData as CountryData2024

      if (this.personalGroupGrowthAllCriteriaMet && personalGroupGrowthMaintaining && personalGroupGrowthPrqCurrent === 12) {
        const { personalGroupGrowthPrqCurrent } = personalGroupGrowthStore
        return NumberDecimalFormat(performanceBonus * personalGroupGrowthPrqCurrent * data.personalGroupGrowth.multiplierMaintain12!)
      }
    }

    return 0
  }

  get personalGroupGrowthMonthlyAmount(): number {
    const { store } = this
    const { personalGroupGrowthStore } = store
    const { personalGroupGrowthActive } = personalGroupGrowthStore

    if (!personalGroupGrowthActive) { return 0 }
    if (this.personalGroupGrowthMonthlyPrePaymentIncentive > 0) {
      return NumberDecimalFormat(this.personalGroupGrowthMonthlyPrePaymentIncentive)
    }

    return 0
  }

  get personalGroupGrowthMonthlyPrePaymentIncentive(): number {
    const { store } = this
    const { performanceBonus } = store

    if (this.personalGroupGrowthAllCriteriaMet) {
      return performanceBonus * this.personalGroupGrowthPerformanceBonusMonthlyMultipier
    }

    return 0
  }

  get personalGroupGrowthAnnualTotalIncentive(): number {
    const { store } = this
    const { performanceBonus, personalGroupGrowthStore } = store
    const { personalGroupGrowthPrqCurrent } = personalGroupGrowthStore

    if (this.personalGroupGrowthAllCriteriaMet) {
      return NumberDecimalFormat(performanceBonus * this.personalGroupGrowthBonusAnualMultipier * personalGroupGrowthPrqCurrent)
    }

    return 0
  }

  get personalGroupGrowthAllCriteriaMet(): boolean {
    const { store } = this
    const { personalGroupGrowthStore } = store
    const { personalGroupGrowthPqMonth, personalGroupGrowthAnnualPPVCriteria } = this
    const { personalGroupGrowthActive, personalGroupGrowthTracking, personalGroupGrowthPrqGrowth } = personalGroupGrowthStore

    if (!personalGroupGrowthActive) { return false }

    return personalGroupGrowthPqMonth && personalGroupGrowthAnnualPPVCriteria && personalGroupGrowthTracking && personalGroupGrowthPrqGrowth !== PrqGrowthValue.DROP
  }

  get personalGroupGrowthAnnualPPVCriteria(): boolean {
    const { store } = this
    const { personalAnnualPV, globalStore, personalGroupGrowthStore } = store
    const { country } = globalStore
    const { personalGroupGrowthActive } = personalGroupGrowthStore

    if (!personalGroupGrowthActive) { return false }
    if (
      (personalAnnualPV >= Constants.ANNUAL_PPV_EUROPE && (country !== Country.AU && country !== Country.NZ)) ||
      (personalAnnualPV >= Constants.ANNUAL_PPV_ANZ && (country === Country.AU || country === Country.NZ))
    ) {
      return true
    }

    return false
  }

  get personalGroupGrowthPqMonth(): boolean {
    const { store } = this
    const { countryHelper } = store

    return countryHelper.personalGroupGrowthPqMonth
  }

  get personalGroupGrowthAnnualPPVRequirement(): number {
    const { store } = this
    const { countryData, versionHelper } = store
    const { version2024 } = versionHelper

    if (version2024) {
      const data = countryData as CountryData2024
      return data.personalGroupGrowthAnnualVolume!
    }

    return 0
  }

}
